/**
 * TermsOfService - Terms of Service page
 */

import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  AppBar,
  Toolbar,
  Divider
} from '@mui/material';
import { ArrowBack as BackIcon } from '@mui/icons-material';

interface TermsOfServiceProps {
  onBack: () => void;
}

const TermsOfService: React.FC<TermsOfServiceProps> = ({ onBack }) => {
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa' }}>
      <AppBar 
        position="sticky" 
        elevation={0}
        sx={{ 
          bgcolor: '#003399',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
        }}
      >
        <Toolbar>
          <Button
            startIcon={<BackIcon />}
            onClick={onBack}
            sx={{ color: 'white' }}
          >
            Back
          </Button>
          <Typography variant="h6" sx={{ ml: 2, color: 'white' }}>
            Terms of Service
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ py: 6 }}>
        <Paper elevation={0} sx={{ p: { xs: 3, md: 6 }, borderRadius: 3 }}>
          <Typography variant="h3" sx={{ fontWeight: 700, color: '#003399', mb: 2 }}>
            Terms of Service
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
            Last updated: January 2026
          </Typography>

          <Divider sx={{ mb: 4 }} />

          <Typography variant="h5" sx={{ fontWeight: 600, color: '#003399', mb: 2 }}>
            1. Acceptance of Terms
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, color: '#444' }}>
            By accessing and using the Erasmus+ Project Generator service ("Service"), you accept and agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our Service.
          </Typography>

          <Typography variant="h5" sx={{ fontWeight: 600, color: '#003399', mb: 2 }}>
            2. Description of Service
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, color: '#444' }}>
            The Erasmus+ Project Generator is an AI-powered platform that assists users in creating project proposals for Erasmus+ funding applications. The Service generates project content, structures, and documentation based on user inputs. The generated content is intended as a starting point and should be reviewed, customized, and validated by the user before any official submission.
          </Typography>

          <Typography variant="h5" sx={{ fontWeight: 600, color: '#003399', mb: 2 }}>
            3. User Responsibilities
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8, color: '#444' }}>
            Users are responsible for:
          </Typography>
          <Box component="ul" sx={{ mb: 3, pl: 3, color: '#444' }}>
            <li><Typography variant="body1" sx={{ lineHeight: 1.8 }}>Providing accurate and truthful information when using the Service</Typography></li>
            <li><Typography variant="body1" sx={{ lineHeight: 1.8 }}>Reviewing and validating all generated content before submission to any National Agency</Typography></li>
            <li><Typography variant="body1" sx={{ lineHeight: 1.8 }}>Ensuring compliance with Erasmus+ programme guidelines and requirements</Typography></li>
            <li><Typography variant="body1" sx={{ lineHeight: 1.8 }}>Maintaining the confidentiality of their account credentials</Typography></li>
            <li><Typography variant="body1" sx={{ lineHeight: 1.8 }}>Using the Service in accordance with applicable laws and regulations</Typography></li>
          </Box>

          <Typography variant="h5" sx={{ fontWeight: 600, color: '#003399', mb: 2 }}>
            4. Intellectual Property
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, color: '#444' }}>
            The content generated through our Service becomes the property of the user upon generation. However, users acknowledge that similar content may be generated for other users. The platform, its design, features, and underlying technology remain the intellectual property of Erasmus+ Project Generator.
          </Typography>

          <Typography variant="h5" sx={{ fontWeight: 600, color: '#003399', mb: 2 }}>
            5. Paid Plans and Payments
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, color: '#444' }}>
            Certain features of the Service require a paid plan. Paid plans (e.g. Basic, TOP) are <strong>one-time payments</strong>, not monthly subscriptions. Prices are shown VAT included where indicated. Credits purchased (executions) must be <strong>used within 30 days of purchase</strong>; unused credits after that period are not carried over. Payments are non-refundable except as required by law.
          </Typography>

          <Typography variant="h5" sx={{ fontWeight: 600, color: '#003399', mb: 2 }}>
            6. Disclaimer of Warranties
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, color: '#444' }}>
            The Service is provided "as is" without warranties of any kind. We do not guarantee that generated content will result in successful funding applications. The success of any Erasmus+ application depends on many factors beyond the scope of this Service, including the evaluation criteria of the relevant National Agency.
          </Typography>

          <Typography variant="h5" sx={{ fontWeight: 600, color: '#003399', mb: 2 }}>
            7. Limitation of Liability
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, color: '#444' }}>
            To the maximum extent permitted by law, we shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use of the Service, including but not limited to loss of funding opportunities, loss of data, or business interruption.
          </Typography>

          <Typography variant="h5" sx={{ fontWeight: 600, color: '#003399', mb: 2 }}>
            8. Modifications to Terms
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, color: '#444' }}>
            We reserve the right to modify these Terms of Service at any time. Users will be notified of significant changes via email or through the Service. Continued use of the Service after such modifications constitutes acceptance of the updated terms.
          </Typography>

          <Typography variant="h5" sx={{ fontWeight: 600, color: '#003399', mb: 2 }}>
            9. Governing Law
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, color: '#444' }}>
            These Terms of Service shall be governed by and construed in accordance with the laws of Italy and the European Union. Any disputes arising from these terms shall be subject to the exclusive jurisdiction of the courts of Italy.
          </Typography>

          <Typography variant="h5" sx={{ fontWeight: 600, color: '#003399', mb: 2 }}>
            10. Contact Information
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, color: '#444' }}>
            For any questions regarding these Terms of Service, please contact us at:
          </Typography>
          <Paper 
            elevation={0} 
            sx={{ 
              p: 3, 
              bgcolor: 'rgba(0, 51, 153, 0.05)', 
              borderRadius: 2,
              border: '1px solid rgba(0, 51, 153, 0.1)'
            }}
          >
            <Typography variant="body1" sx={{ fontWeight: 600, color: '#003399' }}>
              Email: info@marinuzzi.it
            </Typography>
          </Paper>

          <Box sx={{ mt: 6, textAlign: 'center' }}>
            <Button
              variant="contained"
              onClick={onBack}
              sx={{
                bgcolor: '#003399',
                px: 4,
                py: 1.5,
                '&:hover': { bgcolor: '#002266' }
              }}
            >
              Back to Home
            </Button>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default TermsOfService;
